import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  FaCoffee, 
  FaWifi, 
  FaUtensils, 
  FaClock, 
  FaMapMarkerAlt, 
  FaInstagram, 
  FaStar, 
  FaEnvelope, 
  FaPhoneAlt 
} from 'react-icons/fa';
import { MdOutlineDeliveryDining } from 'react-icons/md';
import { IoMdCafe } from 'react-icons/io';
import { BsCreditCard2Front, BsLightningCharge } from 'react-icons/bs';
import emailjs from '@emailjs/browser';
import { FaRegCalendarAlt } from 'react-icons/fa';

const CafeLanding = () => {
  // Get cafe slug from URL parameters
  const { cafeSlug } = useParams();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    reservationDate: '',
    reservationTime: '',
    partySize: ''
  });

  // Example cafe data - in a real app, this would come from an API based on the slug
  const cafeData = {
    'cyber-brew': {
      name: 'Cyber Brew',
      tagline: 'Where Code Meets Coffee',
      description: 'A futuristic coffee experience with unique tech-inspired beverages and a space designed for digital nomads.',
      image: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1200&q=80',
      rating: 4.8,
      reviews: 178,
      stats: {
        established: '2018',
        speciality: 'Digital Blend',
        seating: '45 seats',
        wifi: 'Gigabit'
      },
      galleryImages: [
        'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1513267048331-5611cad62e41?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=600&h=400&q=80'
      ],
      events: [
        {
          name: 'Code & Coffee Meetup',
          date: 'Every Tuesday, 7PM',
          description: 'Join fellow developers for coding sessions with unlimited coffee'
        },
        {
          name: 'Tech Startup Pitch Night',
          date: 'Last Friday of month, 6PM',
          description: 'Pitch your tech startup idea to potential investors'
        }
      ]
    },
    'neon-espresso': {
      name: 'Neon Espresso',
      tagline: 'Illuminating Your Coffee Experience',
      description: 'Where neon aesthetics meet premium coffee. Our signature drinks glow under blacklight for a truly unique cafe experience.',
      image: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1200&q=80',
      rating: 4.7,
      reviews: 156,
      stats: {
        established: '2020',
        speciality: 'Neon Latte',
        seating: '35 seats',
        wifi: '5G Enabled'
      },
      galleryImages: [
        'https://images.unsplash.com/photo-1513267048331-5611cad62e41?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1482350325005-eda5e677279b?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1592663527359-cf6642f54cff?auto=format&fit=crop&w=600&h=400&q=80'
      ],
      events: [
        {
          name: 'Neon Nights DJ Set',
          date: 'Every Friday, 8PM',
          description: 'Enjoy ambient electronic music with special glow-in-the-dark cocktails'
        },
        {
          name: 'Blacklight Art Exhibition',
          date: 'First Saturday of month, 7PM',
          description: 'Local artists showcase UV-reactive artworks'
        }
      ]
    },
    'matrix-mocha': {
      name: 'Matrix Mocha',
      tagline: 'Take the Red Cup or Blue Cup',
      description: 'A reality-bending cafe experience with themed coffees and pastries in a setting that feels like you\'ve stepped into virtual reality.',
      image: 'https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?auto=format&fit=crop&w=1200&q=80',
      rating: 4.9,
      reviews: 212,
      stats: {
        established: '2019',
        speciality: 'Red/Blue Mochas',
        seating: '50 seats',
        wifi: 'Neural Network'
      },
      galleryImages: [
        'https://images.unsplash.com/photo-1498804103079-a6351b050096?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=600&h=400&q=80'
      ],
      events: [
        {
          name: 'VR Gaming Night',
          date: 'Every Wednesday, 6PM',
          description: 'Try the latest VR games with special themed drinks'
        },
        {
          name: 'Sci-Fi Movie Marathon',
          date: 'Third Saturday of month, 3PM',
          description: 'Classic and new sci-fi films with unlimited popcorn'
        }
      ]
    },
    'quantum-coffee': {
      name: 'Quantum Coffee',
      tagline: 'Coffee in Multiple Dimensions',
      description: 'Experience coffee like never before with our quantum-themed cafe that offers unique brewing methods and flavor combinations.',
      image: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=1200&q=80',
      rating: 4.6,
      reviews: 142,
      stats: {
        established: '2021',
        speciality: 'Quantum Brew',
        seating: '40 seats',
        wifi: 'Quantum Secured'
      },
      galleryImages: [
        'https://images.unsplash.com/photo-1498804103079-a6351b050096?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=600&h=400&q=80'
      ],
      events: [
        {
          name: 'Physics & Froth',
          date: 'Every Thursday, 7PM',
          description: 'Science discussions with professors from local universities'
        },
        {
          name: 'Coffee Tasting Workshop',
          date: 'Second Saturday of month, 2PM',
          description: 'Learn about different coffee origins and brewing methods'
        }
      ]
    },
    'data-drip': {
      name: 'Data Drip',
      tagline: 'Analyzing Every Drop',
      description: 'A data-driven cafe experience where each cup is brewed with mathematical precision for optimal flavor extraction.',
      image: 'https://images.unsplash.com/photo-1503481766315-7a586b20f66d?auto=format&fit=crop&w=1200&q=80',
      rating: 4.8,
      reviews: 189,
      stats: {
        established: '2020',
        speciality: 'Algorithm Brew',
        seating: '42 seats',
        wifi: 'Encrypted'
      },
      galleryImages: [
        'https://images.unsplash.com/photo-1513267048331-5611cad62e41?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1461988320302-91bde64fc8e4?auto=format&fit=crop&w=600&h=400&q=80',
        'https://images.unsplash.com/photo-1467189386127-c4e5e31ee213?auto=format&fit=crop&w=600&h=400&q=80'
      ],
      events: [
        {
          name: 'Data Science Meetup',
          date: 'Every Monday, 6:30PM',
          description: 'Informal gathering of data scientists and analysts'
        },
        {
          name: 'Coding Challenge Night',
          date: 'Last Thursday of month, 7PM',
          description: 'Compete in timed coding challenges to win free coffee for a month'
        }
      ]
    }
  };

  // Create an effect to handle cafe slug if present
  useEffect(() => {
    if (cafeSlug) {
      // Example: Update document title based on slug
      const cafeName = cafeData[cafeSlug]?.name || 'Cafe';
      document.title = `${cafeName} | CyberNest`;
    }
  }, [cafeSlug, cafeData]);

  // Get current cafe data if slug exists and is valid
  const currentCafe = cafeSlug && cafeData[cafeSlug] ? cafeData[cafeSlug] : null;

  // Modal state
  const [showReservationModal, setShowReservationModal] = useState(false);
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ 
      submitted: false, 
      submitting: true,
      error: false,
      message: ''
    });
    
    // EmailJS service configuration
    emailjs.send(
      'service_xxxxxxx', // Service ID
      'template_xxxxxxx', // Template ID
      {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        reservation_date: formData.reservationDate,
        reservation_time: formData.reservationTime,
        party_size: formData.partySize
      },
      'YOUR_PUBLIC_KEY' // Public Key
    )
      .then(() => {
        setFormStatus({ 
          submitted: true, 
          submitting: false,
          error: false,
          message: 'Thank you! Your reservation request has been received. We\'ll contact you shortly to confirm.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          reservationDate: '',
          reservationTime: '',
          partySize: ''
        });
      })
      .catch(() => {
        setFormStatus({ 
          submitted: false, 
          submitting: false, 
          error: true,
          message: 'There was an error submitting your request. Please try again or contact us directly.'
        });
      });
  };

  // Cafe features
  const features = [
    { 
      icon: <FaWifi />, 
      title: "High-Speed WiFi", 
      description: "Free gigabit WiFi for all customers with comfortable workspaces."
    },
    { 
      icon: <FaUtensils />, 
      title: "Artisan Food", 
      description: "Locally-sourced ingredients for our freshly prepared meals and snacks."
    },
    { 
      icon: <FaClock />, 
      title: "Extended Hours", 
      description: "Open from early morning until late evening to accommodate all schedules."
    },
    { 
      icon: <MdOutlineDeliveryDining />, 
      title: "Delivery Options", 
      description: "Get your favorite coffee and food delivered right to your door."
    },
    { 
      icon: <BsCreditCard2Front />, 
      title: "Digital Payments", 
      description: "Accept all major payment methods including cryptocurrency."
    },
    { 
      icon: <IoMdCafe />, 
      title: "Specialty Coffees", 
      description: "Unique blends and brewing methods you won't find anywhere else."
    }
  ];

  // Menu items
  const menuCategories = [
    {
      name: "Coffee",
      items: [
        { name: "Neon Latte", price: "₹180", description: "Espresso with steamed milk and neon-colored syrup" },
        { name: "Cyber Americano", price: "₹150", description: "Strong espresso diluted with hot water" },
        { name: "Digital Mocha", price: "₹210", description: "Espresso with chocolate and foamed milk" },
        { name: "Quantum Cold Brew", price: "₹220", description: "Slow-steeped coffee served over ice" },
        { name: "Binary Cappuccino", price: "₹190", description: "Espresso with equal parts steamed milk and foam" },
        { name: "Neural Network Nitro", price: "₹240", description: "Nitrogen-infused cold brew with a creamy head" }
      ]
    },
    {
      name: "Food",
      items: [
        { name: "Matrix Croissant", price: "₹160", description: "Butter croissant with a twist of cinnamon" },
        { name: "Pixel Sandwich", price: "₹250", description: "Avocado, cheese, and tomato on sourdough" },
        { name: "Binary Bagel", price: "₹190", description: "Cream cheese and smoked salmon on a fresh bagel" },
        { name: "Cloud Pancakes", price: "₹220", description: "Fluffy pancakes with berry compote and maple syrup" },
        { name: "Firewall Frittata", price: "₹280", description: "Spicy vegetable frittata with jalapeños and cheddar" },
        { name: "Blockchain Brownies", price: "₹150", description: "Rich chocolate brownies with a crunchy top layer" }
      ]
    },
    {
      name: "Signature Drinks",
      items: [
        { name: "Holographic Smoothie", price: "₹260", description: "Layered fruit smoothie with shimmering effect" },
        { name: "VR Hot Chocolate", price: "₹230", description: "Rich hot chocolate with marshmallow art" },
        { name: "Cyberpunk Chai", price: "₹210", description: "Spiced chai latte with electric blue spirulina" },
        { name: "Augmented Reality Tea", price: "₹180", description: "Color-changing tea with butterfly pea flower" }
      ]
    }
  ];

  // Testimonials from cafe owners
  const testimonials = [
    {
      name: "Priya Sharma",
      cafe: "Digital Bean Cafe",
      image: "https://i.pravatar.cc/150?img=48",
      quote: "CyberNest transformed our cafe's online presence completely. Our online orders increased by 70% within the first month!"
    },
    {
      name: "Raj Malhotra",
      cafe: "Byte Bistro",
      image: "https://i.pravatar.cc/150?img=67",
      quote: "The online ordering system integrated seamlessly with our POS. The customer experience is incredible!"
    },
    {
      name: "Ananya Patel",
      cafe: "Circuit Coffee",
      image: "https://i.pravatar.cc/150?img=32",
      quote: "The visual design captures our cafe's vibe perfectly. Our customers love the online experience as much as visiting us."
    }
  ];

  // FAQs
  const faqs = [
    {
      question: "How long does it take to build a cafe website?",
      answer: "Typically 2-3 weeks from concept to launch, depending on the complexity of features required."
    },
    {
      question: "Do you offer online ordering integration?",
      answer: "Yes! We integrate with popular POS systems and can also build custom ordering solutions tailored to your cafe's needs."
    },
    {
      question: "Can I update the menu myself?",
      answer: "Absolutely. We build an intuitive admin panel that makes it easy to update menu items, prices, and availability."
    },
    {
      question: "Do you offer ongoing support?",
      answer: "Yes, all our cafe websites come with 3 months of free support and maintenance, with affordable monthly plans afterward."
    }
  ];

  return (
    <div className="bg-cyber-dark overflow-hidden">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="w-full h-full bg-[linear-gradient(90deg,rgba(157,78,221,0.03)_1px,transparent_1px),linear-gradient(0deg,rgba(157,78,221,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-cyber-dark to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-cyber-dark to-transparent"></div>
          
          {/* Animated particles */}
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className="absolute bg-cyber-pink rounded-full opacity-20 animate-float"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto px-6 pt-32 pb-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {currentCafe ? (
                <>
                  <h1 className="text-5xl md:text-6xl font-orbitron font-bold mb-6">
                    <span className="text-white">{currentCafe.name}</span>
                  </h1>
                  <h2 className="text-2xl text-cyber-pink font-orbitron mb-6">
                    {currentCafe.tagline}
                  </h2>
                  <p className="text-xl text-gray-300 mb-8">
                    {currentCafe.description}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-5xl md:text-6xl font-orbitron font-bold mb-6">
                    <span className="text-white">Digital</span> <br />
                    <span className="text-cyber-pink">Café Experiences</span>
                  </h1>
                  <p className="text-xl text-gray-300 mb-8">
                    Transform your café into a cyberpunk paradise with our cutting-edge website solutions 
                    for modern coffee shops, bistros, and specialty cafés.
                  </p>
                </>
              )}
              
              <div className="flex flex-wrap gap-4">
                {currentCafe ? (
                  <>
                    <button
                      onClick={() => setShowReservationModal(true)}
                      className="cyber-button"
                    >
                      <span>Reserve a Table</span>
                    </button>
                    <a 
                      href="#menu"
                      className="cyber-button-outline"
                    >
                      <span>View Menu</span>
                    </a>
                  </>
                ) : (
                  <>
                    <a 
                      href="#cafes"
                      className="cyber-button"
                    >
                      <span>View Our Cafés</span>
                    </a>
                    <a 
                      href="#contact"
                      className="cyber-button-outline"
                    >
                      <span>Contact Us</span>
                    </a>
                  </>
                )}
              </div>
              
              {!currentCafe && (
                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <span className="px-3 py-1 bg-cyber-pink/20 border border-cyber-pink rounded-full text-sm text-cyber-pink font-medium">
                    New Service
                  </span>
                  <p className="text-cyber-neon text-sm">Specialized websites for cafés, coffee shops, and bistros!</p>
                </div>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="cyber-card p-1 overflow-hidden">
                {currentCafe ? (
                  <img 
                    src={currentCafe.image} 
                    alt={currentCafe.name} 
                    className="w-full h-auto rounded object-cover aspect-video"
                  />
                ) : (
                  <img 
                    src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1000&q=80" 
                    alt="Cyberpunk café concept" 
                    className="w-full h-auto rounded"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-tr from-cyber-dark/80 via-transparent to-transparent rounded"></div>
                
                {!currentCafe && (
                  <>
                    {/* Floating badges */}
                    <div className="absolute top-4 left-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-pink rounded-full text-white flex items-center gap-2">
                      <BsLightningCharge className="text-cyber-pink" />
                      <span>Fast Loading</span>
                    </div>
                    
                    <div className="absolute bottom-4 right-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-neon rounded-full text-white flex items-center gap-2">
                      <FaCoffee className="text-cyber-neon" />
                      <span>Café Specialists</span>
                    </div>
                  </>
                )}
              </div>
              
              {currentCafe ? (
                <div className="grid grid-cols-2 gap-6 mt-6">
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-pink mb-1">Est. {currentCafe.stats.established}</span>
                    <span className="text-xs text-center text-gray-300">Year Founded</span>
                  </div>
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-neon mb-1">{currentCafe.stats.speciality}</span>
                    <span className="text-xs text-center text-gray-300">Signature Drink</span>
                  </div>
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-purple mb-1">{currentCafe.stats.seating}</span>
                    <span className="text-xs text-center text-gray-300">Capacity</span>
                  </div>
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-blue mb-1">{currentCafe.stats.wifi}</span>
                    <span className="text-xs text-center text-gray-300">WiFi Speed</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Stats floating */}
                  <div className="absolute -bottom-10 -left-10 cyber-card p-4 flex flex-col items-center justify-center w-32 h-32">
                    <span className="text-3xl font-bold text-cyber-neon">15+</span>
                    <span className="text-xs text-center text-gray-300">Café Websites</span>
                  </div>
                  
                  <div className="absolute -top-5 -right-5 cyber-card p-4 flex flex-col items-center justify-center w-28 h-28">
                    <span className="text-3xl font-bold text-cyber-pink">24/7</span>
                    <span className="text-xs text-center text-gray-300">Online Ordering</span>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {currentCafe && (
        <section className="py-20 bg-cyber-dark/80 border-y border-cyber-purple/20" id="menu">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Our <span className="text-cyber-neon">Menu</span></span>
              </h2>
              <p className="text-gray-300 max-w-3xl mx-auto">
                Explore our selection of cyberpunk-inspired coffee creations and futuristic food options.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              {menuCategories.map((category, index) => (
                <motion.div 
                  key={category.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="cyber-card p-[2px]"
                >
                  <div className="bg-cyber-dark p-8 h-full">
                    <h3 className="text-2xl font-orbitron text-cyber-pink mb-6">{category.name}</h3>
                    <div className="space-y-6">
                      {category.items.map(item => (
                        <div key={item.name} className="border-b border-cyber-purple/20 pb-4 last:border-none last:pb-0">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="text-white font-medium">{item.name}</h4>
                            <span className="text-cyber-neon font-orbitron">{item.price}</span>
                          </div>
                          <p className="text-gray-400 text-sm">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section - New */}
      {currentCafe && (
        <section className="py-20 bg-cyber-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Café <span className="text-cyber-purple">Gallery</span></span>
              </h2>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {currentCafe.galleryImages.map((img, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="cyber-card p-[2px] overflow-hidden"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={img} 
                      alt={`${currentCafe.name} interior ${index + 1}`} 
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events Section - New */}
      {currentCafe && (
        <section className="py-20 bg-cyber-dark/80 border-y border-cyber-purple/20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Upcoming <span className="text-cyber-neon">Events</span></span>
              </h2>
              <p className="text-gray-300 max-w-3xl mx-auto">
                Join us for special events at {currentCafe.name}.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {currentCafe.events.map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="cyber-card p-[2px]"
                >
                  <div className="p-6 bg-cyber-dark h-full">
                    <h3 className="text-xl font-orbitron text-cyber-pink mb-2">{event.name}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <FaRegCalendarAlt className="text-cyber-neon" />
                      <span className="text-white">{event.date}</span>
                    </div>
                    <p className="text-gray-300">{event.description}</p>
                    <button className="mt-4 px-4 py-2 border border-cyber-pink text-cyber-pink hover:bg-cyber-pink/10 transition-colors">
                      RSVP Now
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Location Section - New */}
      {currentCafe && (
        <section className="py-20 bg-cyber-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Find <span className="text-cyber-blue">Us</span></span>
              </h2>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="cyber-card p-[2px]">
                <div className="aspect-[4/3] bg-cyber-dark/50 relative p-1">
                  {/* Mock map with cyberpunk styling */}
                  <div className="w-full h-full bg-[radial-gradient(circle_at_center,#1a1a2e,#000)]">
                    <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(125,125,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(125,125,255,0.1)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8">
                      <div className="w-full h-full rounded-full bg-cyber-pink/30 animate-ping"></div>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-cyber-pink"></div>
                    </div>
                    <div className="absolute bottom-4 right-4 text-cyber-neon text-sm font-mono">
                      {currentCafe.name} • LOCATION
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-orbitron text-white mb-6">Visit {currentCafe.name}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-cyber-dark/80 border border-cyber-neon rounded-full p-2 mt-1">
                      <FaMapMarkerAlt className="text-cyber-neon" />
                    </div>
                    <div>
                      <h4 className="text-cyber-neon font-medium mb-1">Address</h4>
                      <p className="text-white">42 Cyber Lane</p>
                      <p className="text-white">Neon District, Digital City</p>
                      <p className="text-white">400101</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-cyber-dark/80 border border-cyber-pink rounded-full p-2 mt-1">
                      <FaClock className="text-cyber-pink" />
                    </div>
                    <div>
                      <h4 className="text-cyber-pink font-medium mb-1">Hours</h4>
                      <p className="text-white">Monday - Friday: 7AM - 10PM</p>
                      <p className="text-white">Weekends: 8AM - Midnight</p>
                    </div>
                  </div>
                  
                  <button className="px-6 py-3 bg-cyber-neon text-cyber-dark font-medium font-orbitron hover:bg-cyber-neon/90 transition-colors mt-4">
                    Get Directions
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Showcase cafes when on main page */}
      {!currentCafe && (
        <section id="cafes" className="py-20 bg-cyber-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Featured <span className="text-cyber-neon">Cafés</span></span>
              </h2>
              <p className="text-gray-300 max-w-3xl mx-auto">
                Explore our collection of futuristic cafés with stunning websites designed by CyberNest.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              {Object.entries(cafeData).map(([slug, cafe], index) => (
                <motion.a
                  href={`/cafe/${slug}`}
                  key={slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group cyber-card p-[2px] overflow-hidden block"
                >
                  <div className="bg-cyber-dark h-full">
                    <div className="relative overflow-hidden h-60">
                      <img 
                        src={cafe.image} 
                        alt={cafe.name} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-cyber-dark via-transparent to-transparent"></div>
                      
                      <div className="absolute top-4 right-4 flex items-center gap-1 px-2 py-1 bg-cyber-dark/80 rounded-full">
                        <FaStar className="text-cyber-neon text-sm" />
                        <span className="text-white text-sm">{cafe.rating}</span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-orbitron text-white mb-2 group-hover:text-cyber-pink transition-colors">{cafe.name}</h3>
                      <p className="text-sm text-gray-400 mb-4">{cafe.tagline}</p>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-cyber-neon text-sm font-medium">View Details</span>
                        <span className="text-gray-500 text-xs">{cafe.reviews} reviews</span>
                      </div>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Testimonials - New */}
      {!currentCafe && (
        <section className="py-20 bg-cyber-dark/80 border-y border-cyber-purple/20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Café Owners <span className="text-cyber-pink">Love Us</span></span>
              </h2>
              <p className="text-gray-300 max-w-3xl mx-auto">
                See what café owners across India are saying about their CyberNest website experience.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="cyber-card p-[2px]"
                >
                  <div className="p-8 bg-cyber-dark h-full flex flex-col">
                    <div className="flex items-center gap-4 mb-6">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name} 
                        className="w-16 h-16 rounded-full border-2 border-cyber-neon"
                      />
                      <div>
                        <p className="text-white font-medium">{testimonial.name}</p>
                        <p className="text-cyber-neon">{testimonial.cafe}</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 italic flex-grow">"{testimonial.quote}"</p>
                    
                    <div className="flex mt-6">
                      {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className="text-cyber-pink" />
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* FAQs - New */}
      {!currentCafe && (
        <section className="py-20 bg-cyber-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Frequently Asked <span className="text-cyber-blue">Questions</span></span>
              </h2>
              <p className="text-gray-300 max-w-3xl mx-auto">
                Everything you need to know about our café website services.
              </p>
            </motion.div>
            
            <div className="max-w-4xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="cyber-card p-[1px]"
                >
                  <div className="p-6 bg-cyber-dark">
                    <h3 className="text-xl font-orbitron text-cyber-neon mb-3">{faq.question}</h3>
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* CTA Section - New */}
      {!currentCafe && (
        <section className="py-24 bg-cyber-dark/90 border-t border-cyber-purple/20">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="mb-16"
              >
                <h2 className="text-5xl font-orbitron font-bold mb-6">
                  <span className="text-white">Ready to <span className="text-cyber-pink">Digitize</span> Your Café?</span>
                </h2>
                <p className="text-gray-300 text-xl max-w-3xl mx-auto">
                  Join hundreds of cafés across India that have transformed their business with a CyberNest website.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex flex-col sm:flex-row items-center justify-center gap-6"
              >
                <a href="#contact" className="cyber-button">
                  <span>Get Started</span>
                </a>
                <a href="/pricing" className="px-8 py-4 border border-cyber-neon text-cyber-neon hover:bg-cyber-neon/10 transition-colors">
                  View Pricing
                </a>
              </motion.div>
            </div>
          </div>
        </section>
      )}
      
      {/* Features section */}
      <section className="py-20 bg-cyber-dark/90">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-white">Café Website <span className="text-cyber-purple">Features</span></span>
            </h2>
            <p className="text-gray-300 max-w-3xl mx-auto">
              Our café websites come loaded with specialized features to help you connect with customers and grow your business.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="cyber-card p-[2px]"
              >
                <div className="p-6 bg-cyber-dark h-full flex flex-col">
                  <div className="bg-gradient-to-br from-cyber-pink to-cyber-purple h-12 w-12 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-orbitron text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400 flex-grow">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Contact section */}
      <section id="contact" className="py-20 bg-cyber-dark">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-orbitron font-bold mb-6">
                <span className="text-white">Let's <span className="text-cyber-neon">Connect</span></span>
              </h2>
              <p className="text-gray-300 mb-8">
                Ready to take your café's online presence to the next level? Get in touch with us today
                for a free consultation and quote.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-cyber-dark border border-cyber-neon h-12 w-12 rounded-full flex items-center justify-center text-cyber-neon">
                    <FaPhoneAlt />
                  </div>
                  <div>
                    <p className="text-gray-400">Call Us</p>
                    <p className="text-white">+91-9468532536</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="bg-cyber-dark border border-cyber-pink h-12 w-12 rounded-full flex items-center justify-center text-cyber-pink">
                    <FaEnvelope />
                  </div>
                  <div>
                    <p className="text-gray-400">Email Us</p>
                    <p className="text-white">cafes@cybernest.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="bg-cyber-dark border border-cyber-purple h-12 w-12 rounded-full flex items-center justify-center text-cyber-purple">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <p className="text-gray-400">Visit Us</p>
                    <p className="text-white">Tech Hub, Sector 14, Gurgaon, India</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="cyber-card p-[2px]">
                <div className="p-6 bg-cyber-dark">
                  <h3 className="text-2xl font-orbitron text-white mb-6">Send a Message</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="text-gray-300 mb-1 block">Your Name</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-cyber-dark border border-cyber-purple/50 focus:border-cyber-purple text-white px-4 py-2 rounded-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="text-gray-300 mb-1 block">Your Email</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-cyber-dark border border-cyber-purple/50 focus:border-cyber-purple text-white px-4 py-2 rounded-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="text-gray-300 mb-1 block">Your Message</label>
                      <textarea 
                        id="message" 
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full bg-cyber-dark border border-cyber-purple/50 focus:border-cyber-purple text-white px-4 py-2 rounded-sm resize-none"
                        required
                      ></textarea>
                    </div>
                    
                    <button 
                      type="submit"
                      disabled={formStatus.submitting}
                      className="cyber-button w-full"
                    >
                      <span>{formStatus.submitting ? 'Sending...' : 'Send Message'}</span>
                    </button>
                    
                    {formStatus.submitted && (
                      <div className="bg-cyber-neon/20 border border-cyber-neon text-white p-4 rounded-sm">
                        {formStatus.message}
                      </div>
                    )}
                    
                    {formStatus.error && (
                      <div className="bg-red-500/20 border border-red-500 text-white p-4 rounded-sm">
                        {formStatus.message}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CafeLanding; 